import React, { useCallback, useEffect, useState } from 'react';
import { Offcanvas, Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import clsx from 'clsx';
import Select from 'react-select';

// Hooks, Schemas, dan Tipe untuk V4
import { useCreateUserV4 } from '../hooks/useCreateUserV4';
import { CreateUserFormData, CreateUserV4Schema } from '../schemas/CreateUserV4Schema';
import { useAuth } from '../../../auth/context/AuthProvider'; // Untuk mendapatkan company_id
import { useRoles } from '../../roles/hooks/useRoles';
import { useGetAllPropertiesV4 } from '../hooks/useGetAllPropertiesV4';
import useSyncUserRoles from '../../../services/user-v4/hooks/useSyncUserRoles';
import { useSyncPropertyV4 } from '../hooks/useSyncPropertyV4';

/**
 * Props untuk komponen CreateUserDialogV4.
 * @param show - Boolean untuk mengontrol visibilitas dialog.
 * @param onClose - Callback function yang dipanggil saat dialog ditutup.
 */
interface CreateUserDialogV4Props {
    show: boolean;
    onClose: () => void;
}

type SelectOption = { value: string; label: string };

/**
 * Komponen dialog (Offcanvas) untuk membuat pengguna baru (Versi 4).
 * Mengelola state formulir sendiri dan menggunakan hook mutasi untuk berinteraksi dengan API.
 *
 * Selain data akun inti, dialog ini juga menawarkan assign role & property LANGSUNG
 * saat create (via switch, default aktif) — supaya admin tidak lupa memberi akses
 * setelah user dibuat. Assignment ini adalah langkah TERPISAH & OPSIONAL setelah user
 * berhasil dibuat (bukan bagian dari payload POST /v4/user), jadi kegagalannya tidak
 * membatalkan pembuatan user — admin tetap bisa assign manual lewat baris tabel.
 */
const CreateUserDialogV4: React.FC<CreateUserDialogV4Props> = ({ show, onClose }) => {
    // Mengambil user yang sedang login untuk mendapatkan company_id
    const { user: authUser } = useAuth();

    // Setup React Hook Form dengan Zod resolver
    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        reset,
        setError
    } = useForm<CreateUserFormData>({
        resolver: zodResolver(CreateUserV4Schema),
        defaultValues: {
            name: '',
            email: '',
            phone: '',
            password: '',
            company_id: '' // Akan diisi oleh useEffect
        }
    });

    // Mengisi company_id secara otomatis saat komponen dimuat atau authUser berubah
    useEffect(() => {
        if (authUser?.company?.id) {
            reset({ company_id: authUser.company.id });
        }
    }, [authUser, reset]);

    // Hook mutasi untuk membuat pengguna
    const { mutate: createUser } = useCreateUserV4();

    // Assign role & property langsung saat create — default aktif, opsional untuk di-skip
    const [assignRoleEnabled, setAssignRoleEnabled] = useState(true);
    const [assignPropertyEnabled, setAssignPropertyEnabled] = useState(true);
    const [selectedRoles, setSelectedRoles] = useState<SelectOption[]>([]);
    const [selectedProperties, setSelectedProperties] = useState<SelectOption[]>([]);

    // Eager-fetch, sama seperti pola di AssignRoleDialogV4 / AssignPropertyDialogV4
    const { data: rolesData, isLoading: isRolesLoading, isError: isRolesError } = useRoles({
        name: 'roles',
        page: 1,
        limit: 100,
        search: '',
    });
    const { data: propertiesData, isLoading: isPropertiesLoading, isError: isPropertiesError } = useGetAllPropertiesV4();

    const { mutateAsync: syncRolesAsync } = useSyncUserRoles();
    const { mutateAsync: syncPropertiesAsync } = useSyncPropertyV4();

    const roleOptions: SelectOption[] = rolesData?.data
        ? rolesData.data.map((role: any) => ({ value: role.name, label: role.name }))
        : [];
    const propertyOptions: SelectOption[] = propertiesData?.data
        ? propertiesData.data.map((property) => ({ value: property.id, label: property.name }))
        : [];

    // Handler untuk menutup dialog, dengan me-reset form
    const handleClose = () => {
        reset(); // Reset form ke nilai default
        setAssignRoleEnabled(true);
        setAssignPropertyEnabled(true);
        setSelectedRoles([]);
        setSelectedProperties([]);
        onClose();
    };

    // Handler untuk submit form
    const onSubmit = useCallback((formData: CreateUserFormData) => {
        const toastId = 'create-user-v4';
        toast.loading('Creating user...', { id: toastId });

        // Buat salinan data form agar bisa diubah
        const payload: Partial<CreateUserFormData> = { ...formData };

        // ================================================================
        // ===        LOGIKA KONDISIONAL UNTUK SUPER ADMIN/DEV          ===
        // ================================================================
        // Jika user yang login tidak memiliki company, hapus field company_id dari data yang akan dikirim.
        if (!authUser?.company) {
            delete payload.company_id;
        }
        // ================================================================

        // Kirim 'payload' yang sudah dimodifikasi, bukan 'formData' asli
        createUser(payload as CreateUserFormData, {
            onSuccess: async (response) => {
                toast.success('User created successfully.', { id: toastId });

                const newUserId = response?.data?.id;
                const roleNames = assignRoleEnabled ? selectedRoles.map(r => r.value) : [];
                const propertyIds = assignPropertyEnabled ? selectedProperties.map(p => p.value) : [];

                // Assign role/property adalah langkah TERPISAH setelah user berhasil dibuat —
                // dijalankan paralel (Promise.allSettled) supaya kegagalan satu langkah tidak
                // menggagalkan yang lain, dan tidak pernah membatalkan user yang SUDAH dibuat.
                if (newUserId && (roleNames.length > 0 || propertyIds.length > 0)) {
                    const assignToastId = 'create-user-v4-assign';
                    toast.loading('Assigning role & property...', { id: assignToastId });

                    const tasks: { step: 'role' | 'property'; promise: Promise<unknown> }[] = [];
                    if (roleNames.length > 0) {
                        tasks.push({ step: 'role', promise: syncRolesAsync({ user_id: newUserId, roles: roleNames }) });
                    }
                    if (propertyIds.length > 0) {
                        tasks.push({ step: 'property', promise: syncPropertiesAsync({ user_ids: [newUserId], property_ids: propertyIds }) });
                    }

                    const results = await Promise.allSettled(tasks.map(t => t.promise));
                    const failedSteps = tasks.filter((_, idx) => results[idx].status === 'rejected').map(t => t.step);

                    if (failedSteps.length === 0) {
                        toast.success('Role & property assigned successfully.', { id: assignToastId });
                    } else {
                        const label = failedSteps.join(' & ');
                        toast.error(`User was created, but failed to assign ${label}. You can assign it manually from the user list.`, { id: assignToastId });
                    }
                }

                handleClose(); // Tutup dan reset dialog setelah berhasil
            },
            onError: (error) => {
                // Penanganan error yang lebih spesifik
                const errorResponse = (error as any)?.response?.data;

                // Jika ini adalah error validasi dari server (422)
                if (errorResponse?.code === 422 && errorResponse?.errors) {
                    toast.error('Please check the form for errors.', { id: toastId });
                    // Petakan error dari server ke field yang sesuai di form
                    for (const [field, messages] of Object.entries(errorResponse.errors)) {
                        setError(field as keyof CreateUserFormData, {
                            type: 'server',
                            message: (messages as string[])[0]
                        });
                    }
                } else {
                    // Untuk error lain (500, network error, dll.)
                    const errorMessage = errorResponse?.message || 'An unexpected error occurred.';
                    toast.error(errorMessage, { id: toastId });
                }
            }
        });
    }, [createUser, handleClose, setError, authUser, assignRoleEnabled, assignPropertyEnabled, selectedRoles, selectedProperties, syncRolesAsync, syncPropertiesAsync]);

    return (
        <Offcanvas show={show} onHide={handleClose} placement="end" backdrop="static" className="offcanvas-responsive">
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>Create New User</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                <form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column h-100">
                    <div className="flex-grow-1">
                        {/* Name Field */}
                        <div className="mb-3">
                            <label htmlFor="name-v4" className="form-label">Full Name</label>
                            <input
                                id="name-v4"
                                type="text"
                                className={clsx("form-control", { 'is-invalid': errors.name })}
                                {...register('name')}
                                placeholder="e.g., John Doe"
                                disabled={isSubmitting}
                            />
                            {errors.name && <div className="invalid-feedback">{errors.name.message}</div>}
                        </div>

                        {/* Email Field */}
                        <div className="mb-3">
                            <label htmlFor="email-v4" className="form-label">Email Address</label>
                            <input
                                id="email-v4"
                                type="email"
                                className={clsx("form-control", { 'is-invalid': errors.email })}
                                {...register('email')}
                                placeholder="e.g., john.doe@example.com"
                                disabled={isSubmitting}
                            />
                            {errors.email && <div className="invalid-feedback">{errors.email.message}</div>}
                        </div>

                        {/* Phone Field */}
                        <div className="mb-3">
                            <label htmlFor="phone-v4" className="form-label">Phone Number</label>
                            <input
                                id="phone-v4"
                                type="text"
                                className={clsx("form-control", { 'is-invalid': errors.phone })}
                                {...register('phone')}
                                placeholder="e.g., 081234567890"
                                disabled={isSubmitting}
                            />
                            {errors.phone && <div className="invalid-feedback">{errors.phone.message}</div>}
                        </div>

                        {/* Password Field */}
                        <div className="mb-3">
                            <label htmlFor="password-v4" className="form-label">Password</label>
                            <input
                                id="password-v4"
                                type="password"
                                className={clsx("form-control", { 'is-invalid': errors.password })}
                                {...register('password')}
                                placeholder="Minimum 8 characters"
                                disabled={isSubmitting}
                            />
                            {errors.password && <div className="invalid-feedback">{errors.password.message}</div>}
                        </div>

                        {/* Company ID (Hidden) - Diisi otomatis */}
                        <input type="hidden" {...register('company_id')} />
                        {errors.company_id && <div className="text-danger small mt-2">{errors.company_id.message}</div>}

                        <hr className="my-4" />
                        <h6 className="mb-3">Access</h6>

                        {/* Assign Role Now */}
                        <div className="mb-3">
                            <Form.Check
                                type="switch"
                                id="assign-role-switch-v4"
                                label="Assign role now"
                                checked={assignRoleEnabled}
                                onChange={(e) => setAssignRoleEnabled(e.target.checked)}
                                disabled={isSubmitting}
                            />
                            {assignRoleEnabled && (
                                <div className="mt-2">
                                    {isRolesError ? (
                                        <div className="text-danger small">Failed to load roles. You can assign a role later from the user list.</div>
                                    ) : (
                                        <Select
                                            isMulti
                                            classNamePrefix="react-select"
                                            options={roleOptions}
                                            value={selectedRoles}
                                            onChange={(opts) => setSelectedRoles(opts as SelectOption[])}
                                            placeholder="Select role(s) to assign..."
                                            isLoading={isRolesLoading}
                                            isDisabled={isSubmitting || isRolesLoading}
                                        />
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Assign Property Now */}
                        <div className="mb-3">
                            <Form.Check
                                type="switch"
                                id="assign-property-switch-v4"
                                label="Assign property now"
                                checked={assignPropertyEnabled}
                                onChange={(e) => setAssignPropertyEnabled(e.target.checked)}
                                disabled={isSubmitting}
                            />
                            {assignPropertyEnabled && (
                                <div className="mt-2">
                                    {isPropertiesError ? (
                                        <div className="text-danger small">Failed to load properties. You can assign a property later from the user list.</div>
                                    ) : (
                                        <Select
                                            isMulti
                                            classNamePrefix="react-select"
                                            options={propertyOptions}
                                            value={selectedProperties}
                                            onChange={(opts) => setSelectedProperties(opts as SelectOption[])}
                                            placeholder="Select propert(y/ies) to assign..."
                                            isLoading={isPropertiesLoading}
                                            isDisabled={isSubmitting || isPropertiesLoading}
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="mt-auto d-flex justify-content-end gap-2">
                        <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={isSubmitting}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                            {isSubmitting ? 'Creating...' : 'Create User'}
                        </button>
                    </div>
                </form>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default CreateUserDialogV4;
